import {addErrorAlert, closeQCO, removeAlert} from "../actions";

// Cache of successfully loaded plugins
const pluginCache = new Map();

// Tracks retry timers for failed plugins
const retryQueue = new Map();

const loadPlugin = (pluginUrl, retries = 3, initialDelayMs = 2000) => {
    // Return cached module immediately if loaded
    if (pluginCache.has(pluginUrl)) {
        return Promise.resolve(pluginCache.get(pluginUrl));
    }

    // If a retry is already scheduled, return that promise
    if (retryQueue.has(pluginUrl)) {
        return retryQueue.get(pluginUrl).promise;
    }

    const attempt = (remaining, delayMs, firstTry = true) => {
        const bust = firstTry ? '' : `?t=${Date.now()}&r=${Math.random()}`;
        return import(/* webpackIgnore: true */ `${pluginUrl}${bust}`)
            .then((module) => {
                pluginCache.set(pluginUrl, module);
                retryQueue.delete(pluginUrl); // Remove from retry queue
                return module;
            })
            .catch(err => {
                if (remaining > 1) {
                    console.log(
                        `Retrying plugin load in ${delayMs}ms: ${pluginUrl} (${remaining - 1} retries left)`,
                        err
                    );

                    // Schedule the retry with exponential backoff
                    const promise = new Promise((resolve, reject) => {
                        setTimeout(() => {
                            attempt(remaining - 1, delayMs * 2, false).then(resolve).catch(reject);
                        }, delayMs);
                    });

                    retryQueue.set(pluginUrl, { promise });
                    return promise;
                }

                retryQueue.delete(pluginUrl);
                throw err;
            });
    };

    const promise = attempt(retries, initialDelayMs);
    retryQueue.set(pluginUrl, { promise });
    return promise;
};

const pluginMiddleware = (store) => (next) => (action) => {
    next(action);
    const state = { ...store.getState() };

    if (Array.isArray(state.rootReducer?.plugins)) {
        for (let plugin of state.rootReducer.plugins) {
            loadPlugin(plugin, 3, 2000) // 3 retries, start with 2s delay
                .then((module) => {
                    module.default(store, state, action);
                })
                .catch(err => {
                    console.log(`Plugin failed to load after all retries: ${plugin}`, err);
                    if(window?.AppConf?.pluginMandatory){
                        store.dispatch(addErrorAlert("Mandatory plugins failed to load, closing POS!", false));
                        window.setTimeout(() => {
                            store.dispatch(removeAlert());
                            let currentState = store.getState();
                            if(currentState.rootReducer.pos !== false){
                                console.log("Closing POS because of missing plugins!");
                                store.dispatch(closeQCO());
                            }
                        }, 7000);
                    }
                });
        }
    }
};

export default pluginMiddleware;
